import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Globe, SpinnerGap, CheckCircle, XCircle, Trash, ArrowClockwise } from '@phosphor-icons/react';

const monoFont = "'IBM Plex Mono', monospace";

interface DomainProfile {
  username: string;
  custom_domain: string | null;
  custom_domain_status: string | null;
  custom_domain_verified: boolean | null;
}

export function AdminDomainSettings() {
  const { session } = useAuth();
  const [profile, setProfile] = useState<DomainProfile | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const fg = 'rgba(247,246,242,0.85)';
  const borderBase = 'rgba(247,246,242,0.15)';

  useEffect(() => {
    if (!session) return;
    (async () => {
      const { data } = await supabase
        .from('user_profiles')
        .select('username, custom_domain, custom_domain_status, custom_domain_verified')
        .eq('user_id', session.user.id)
        .single();
      if (data) setProfile(data as unknown as DomainProfile);
      setLoading(false);
    })();
  }, [session]);

  const handleSave = async () => {
    const domain = domainInput.trim().toLowerCase();
    if (!domain) return;
    setError(null);
    setVerifyMessage(null);
    setSaving(true);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('connect-custom-domain', {
        body: { domain, action: 'connect' },
      });

      if (fnError) {
        const errorContext = (fnError as { context?: Response }).context;
        let msg = fnError.message || 'Erro ao salvar domínio.';
        if (errorContext) {
          const raw = await errorContext.clone().text().catch(() => '');
          try { const p = JSON.parse(raw); if (p?.error) msg = p.error; } catch {}
        }
        setError(msg);
      } else if (data?.error) {
        setError(typeof data.error === 'string' ? data.error : JSON.stringify(data.error));
      } else {
        setProfile(prev => prev ? { ...prev, custom_domain: domain, custom_domain_status: 'pending', custom_domain_verified: false } : prev);
        setDomainInput('');
      }
    } catch {
      setError('Erro de conexão. Tente novamente.');
    }
    setSaving(false);
  };

  const handleVerifyDns = async () => {
    setVerifying(true);
    setError(null);
    setVerifyMessage(null);

    try {
      const { data, error: fnError } = await supabase.functions.invoke('check-domain-status', { body: {} });

      if (fnError) {
        setError('Erro ao verificar DNS.');
      } else if (data) {
        setVerifyMessage(data.message || null);
        if (data.status) {
          setProfile(prev => prev ? {
            ...prev,
            custom_domain_status: data.status,
            custom_domain_verified: data.verified ?? prev.custom_domain_verified,
          } : prev);
        }
      }
    } catch {
      setError('Erro de conexão.');
    }
    setVerifying(false);
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError(null);
    setVerifyMessage(null);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('connect-custom-domain', {
        body: { action: 'remove' },
      });
      if (!fnError && data?.success) {
        setProfile(prev => prev ? { ...prev, custom_domain: null, custom_domain_status: null, custom_domain_verified: false } : prev);
      } else {
        setError('Erro ao remover domínio.');
      }
    } catch {
      setError('Erro de conexão.');
    }
    setRemoving(false);
    setShowConfirmRemove(false);
  };

  if (loading || !profile) return null;

  const status = profile.custom_domain_status;
  const hasDomain = !!profile.custom_domain;
  const isActive = status === 'active';
  const isVerified = status === 'verified' || isActive;

  const statusDisplay = () => {
    switch (status) {
      case 'pending':
        return { icon: '○', label: 'Aguardando verificação DNS', color: 'rgba(247,246,242,0.4)' };
      case 'verified':
        return { icon: '◉', label: 'DNS verificado — conectando...', color: '#F0E614' };
      case 'active':
        return { icon: '✓', label: 'Conectado e ativo', color: '#00E87A' };
      default:
        return { icon: '✗', label: 'Erro na verificação', color: '#FF3F2D' };
    }
  };

  return (
    <div className="mb-8">
      <h3 className="text-[11px] uppercase tracking-wider mb-4" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.45)' }}>
        Domínio Personalizado
      </h3>
      <div className="flex flex-col gap-4">
        {/* Current address */}
        <div className="flex flex-col gap-1">
          <label className="text-[10px] uppercase" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.45)' }}>
            Seu endereço atual
          </label>
          <span className="text-sm" style={{ fontFamily: monoFont, color: fg }}>
            {profile.username}.criaaa.com
          </span>
        </div>

        <div style={{ borderTop: `1px solid ${borderBase}`, margin: '4px 0' }} />

        {hasDomain && isActive ? (
          /* Active domain */
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <CheckCircle size={14} style={{ color: '#00E87A' }} />
              <span className="text-sm" style={{ fontFamily: monoFont, color: '#00E87A' }}>
                {profile.custom_domain} está conectado.
              </span>
            </div>
            <span className="text-[10px] opacity-50" style={{ fontFamily: monoFont, color: fg }}>
              Seu site agora está disponível em {profile.custom_domain}
            </span>
            <RemoveButton onClick={() => setShowConfirmRemove(true)} />
            {showConfirmRemove && (
              <ConfirmRemove
                domain={profile.custom_domain!}
                username={profile.username}
                removing={removing}
                onConfirm={handleRemove}
                onCancel={() => setShowConfirmRemove(false)}
              />
            )}
          </div>
        ) : hasDomain ? (
          /* Pending/verifying state */
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.45)' }}>
                Domínio personalizado
              </label>
              <span className="text-sm" style={{ fontFamily: monoFont, color: fg }}>
                {profile.custom_domain}
              </span>
            </div>

            {/* DNS instructions */}
            <div className="p-3" style={{ border: `1px dashed ${borderBase}` }}>
              <p className="text-[10px] uppercase opacity-50 mb-2" style={{ fontFamily: monoFont, color: fg }}>
                Configure estes registros DNS no seu provedor:
              </p>
              <table className="w-full text-[11px]" style={{ fontFamily: monoFont, color: fg }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${borderBase}` }}>
                    <th className="text-left py-1 pr-4 font-normal opacity-50">Tipo</th>
                    <th className="text-left py-1 pr-4 font-normal opacity-50">Nome</th>
                    <th className="text-left py-1 font-normal opacity-50">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: `1px solid rgba(247,246,242,0.06)` }}>
                    <td className="py-1.5 pr-4">A</td>
                    <td className="py-1.5 pr-4">@</td>
                    <td className="py-1.5" style={{ wordBreak: 'break-all' }}>76.76.21.21</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 pr-4">CNAME</td>
                    <td className="py-1.5 pr-4">www</td>
                    <td className="py-1.5" style={{ wordBreak: 'break-all' }}>cname.vercel-dns.com</td>
                  </tr>
                </tbody>
              </table>
              <p className="text-[9px] opacity-30 mt-2" style={{ fontFamily: monoFont, color: fg }}>
                A propagação DNS pode levar até 48 horas. Clique em "Verificar DNS" para checar o status.
              </p>
            </div>

            {/* Status */}
            {(() => {
              const s = statusDisplay();
              return (
                <div className="flex items-center gap-2">
                  <span style={{ color: s.color, fontFamily: monoFont, fontSize: 14 }}>{s.icon}</span>
                  <span className="text-xs" style={{ fontFamily: monoFont, color: s.color }}>
                    Status: {s.label}
                  </span>
                </div>
              );
            })()}

            {verifyMessage && (
              <span className="text-[10px]" style={{
                fontFamily: monoFont,
                color: isVerified ? '#00E87A' : 'rgba(247,246,242,0.5)',
              }}>
                {verifyMessage}
              </span>
            )}

            {/* Verify DNS button */}
            <button
              onClick={handleVerifyDns}
              disabled={verifying}
              className="uppercase text-[10px] px-4 py-2 self-start flex items-center gap-1.5 disabled:opacity-40"
              style={{
                fontFamily: monoFont,
                border: `1px solid ${fg}`,
                color: fg,
                backgroundColor: 'transparent',
              }}
            >
              {verifying ? <SpinnerGap size={11} weight="thin" className="animate-spin" /> : <ArrowClockwise size={11} weight="thin" />}
              {verifying ? 'Verificando...' : 'Verificar DNS'}
            </button>

            {error && (
              <span className="text-[10px]" style={{ fontFamily: monoFont, color: '#FF3F2D' }}>
                {error}
              </span>
            )}

            <RemoveButton onClick={() => setShowConfirmRemove(true)} />
            {showConfirmRemove && (
              <ConfirmRemove
                domain={profile.custom_domain!}
                username={profile.username}
                removing={removing}
                onConfirm={handleRemove}
                onCancel={() => setShowConfirmRemove(false)}
              />
            )}
          </div>
        ) : (
          /* No domain — input form */
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.45)' }}>
                Domínio personalizado
              </label>
              <div className="flex gap-2">
                <input
                  value={domainInput}
                  onChange={(e) => {
                    setDomainInput(e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, ''));
                    setError(null);
                  }}
                  placeholder="meudominio.com"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 13,
                    fontWeight: 300,
                    padding: '6px 10px',
                    border: `1px solid ${borderBase}`,
                    backgroundColor: 'transparent',
                    color: fg,
                    flex: 1,
                  }}
                />
                <button
                  onClick={handleSave}
                  disabled={saving || !domainInput.trim()}
                  className="uppercase text-[10px] px-4 py-2 flex items-center gap-1 disabled:opacity-40"
                  style={{
                    fontFamily: monoFont,
                    border: `1px solid ${fg}`,
                    color: fg,
                    backgroundColor: 'transparent',
                  }}
                >
                  {saving ? <SpinnerGap size={11} weight="thin" className="animate-spin" /> : <Globe size={11} weight="thin" />}
                  {saving ? 'Conectando...' : 'Conectar'}
                </button>
              </div>
            </div>
            {error && (
              <span className="text-[10px]" style={{ fontFamily: monoFont, color: '#FF3F2D' }}>
                {error}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function RemoveButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="uppercase text-[10px] px-3 py-2 mt-1 self-start flex items-center gap-1"
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        border: '0.5px solid #FF3F2D',
        color: '#FF3F2D',
        backgroundColor: 'transparent',
      }}
    >
      <Trash size={11} weight="thin" />
      Remover Domínio
    </button>
  );
}

function ConfirmRemove({
  domain, username, removing, onConfirm, onCancel,
}: {
  domain: string; username: string; removing: boolean; onConfirm: () => void; onCancel: () => void;
}) {
  const fg = 'rgba(247,246,242,0.85)';
  const borderBase = 'rgba(247,246,242,0.15)';
  const mono = "'IBM Plex Mono', monospace";

  return (
    <div className="p-4 mt-2" style={{ border: '0.5px solid #FF3F2D', backgroundColor: 'rgba(255,63,45,0.05)' }}>
      <p className="text-xs mb-3" style={{ fontFamily: mono, color: fg }}>
        Tem certeza que deseja remover <strong>{domain}</strong>? O site voltará a ser acessível apenas por {username}.criaaa.com.
      </p>
      <div className="flex gap-2">
        <button
          onClick={onConfirm}
          disabled={removing}
          className="uppercase text-[10px] px-3 py-2 flex items-center gap-1"
          style={{ fontFamily: mono, border: '0.5px solid #FF3F2D', color: '#F7F6F2', backgroundColor: '#FF3F2D' }}
        >
          {removing ? <SpinnerGap size={11} weight="thin" className="animate-spin" /> : <Trash size={11} />}
          {removing ? 'Removendo...' : 'Confirmar'}
        </button>
        <button
          onClick={onCancel}
          className="uppercase text-[10px] px-3 py-2"
          style={{ fontFamily: mono, border: `1px solid ${borderBase}`, color: fg, backgroundColor: 'transparent' }}
        >
          Cancelar
        </button>
      </div>
    </div>
  );
}
